import {
  Apps as AppIdIcon,
  ArrowBack as ArrowBackIcon,
  Business as BusinessIcon,
  AssignmentInd as CnpjIcon,
  AdminPanelSettings as CrmIcon,
  Email as EmailIcon,
  Lock as LockIcon,
  Message as MessagesIcon,
  Smartphone as PhoneIdIcon,
  Timer as RpsIcon,
  VpnKey as TokenIcon,
  Shield as VerifyIcon,
  History as VersionIcon,
  Storage as WabaIcon,
} from "@mui/icons-material";
import {
  Box,
  CircularProgress,
  InputAdornment,
  Switch,
  Typography,
} from "@mui/material";
import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAccount } from "../../context/AccountProvider";
import { useToast } from "../../context/ToastContext";
import { createCompany } from "../../services/api";
import * as S from "./AdminCompanySetup.styles";

const AdminCompanySetup: React.FC = () => {
  const { account } = useAccount();
  const { showSuccess, showError } = useToast();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);

  // Estado do formulário
  const [formData, setFormData] = useState({
    name: "",
    cnpj: "",
    email: "",
    company_id: "",
    access_token: "",
    verify_token: "",
    app_identify: "",
    app_version: "",
    waba_id: "",
    phone_number_id: "",
    max_messages_per_day: "250",
    max_requests_per_second: "10",
    whatsapp_test_mode: false,
  });

  // Verificar se o usuário tem permissão pelo domínio do e-mail
  const hasPermission = useMemo(() => {
    if (!account?.email) return false;
    return account.email.toLowerCase().endsWith("@virtusautomation.com");
  }, [account]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      const payload = {
        name: formData.name,
        cnpj: formData.cnpj,
        crm: {
          company_id: formData.company_id.trim(),
        },
        meta: {
          access_token: formData.access_token.trim(),
          verify_token: formData.verify_token.trim(),
          app_identify: formData.app_identify.trim(),
          app_version: formData.app_version.trim(),
          whatsapp_test_mode: formData.whatsapp_test_mode,
          phone_numbers: formData.phone_number_id.trim()
            ? [
                {
                  id: formData.phone_number_id.trim(),
                  number: "", // Será preenchido no primeiro sync
                  objective: "marketing",
                },
              ]
            : [],
          limitation: {
            max_messages_per_day: formData.max_messages_per_day || "250",
            max_requests_per_second: formData.max_requests_per_second || "10",
            last_checked: new Date().toISOString(),
          },
          whatsapp_profile: {
            email: formData.email.trim(),
          },
          whatsapp_business_info: {
            id: formData.phone_number_id.trim() || undefined,
          },
          whatsapp_business_accounts: formData.waba_id.trim()
            ? {
                data: [
                  {
                    id: formData.waba_id.trim(),
                    name: "WhatsApp Business Account",
                  },
                ],
              }
            : undefined,
        },
      };

      await createCompany(payload);
      showSuccess("Empresa cadastrada com sucesso!");
      navigate("/"); // Volta para o messenger
    } catch (err: any) {
      showError(err.message || "Erro ao cadastrar empresa");
    } finally {
      setLoading(false);
    }
  };

  if (!hasPermission) {
    return (
      <S.AdminSetupContainer>
        <S.GlassCard>
          <S.ErrorState>
            <LockIcon />
            <S.Title>Acesso Restrito</S.Title>
            <S.Subtitle sx={{ mb: 4 }}>
              Apenas administradores com domínio{" "}
              <strong>@virtusautomation.com</strong> podem acessar esta página.
            </S.Subtitle>
            <S.SubmitButton
              onClick={() => navigate("/")}
              sx={{ width: "auto", px: 4 }}
            >
              Voltar para o Início
            </S.SubmitButton>
          </S.ErrorState>
        </S.GlassCard>
      </S.AdminSetupContainer>
    );
  }

  return (
    <S.AdminSetupContainer>
      <S.BackButtonWrapper>
        <S.StyledIconButton onClick={() => navigate(-1)}>
          <ArrowBackIcon />
        </S.StyledIconButton>
      </S.BackButtonWrapper>

      <S.GlassCard>
        <form onSubmit={handleSubmit} style={{ width: "100%" }}>
          <S.HeaderSection>
            <S.Title>Setup de Empresa</S.Title>
            <S.Subtitle>
              Inicialize uma nova empresa no ecossistema Virtus WhatsApp com
              todas as configurações necessárias.
            </S.Subtitle>
          </S.HeaderSection>

          <S.SectionsGrid>
            {/* ... Rest of the content remains the same ... */}
            <S.Column>
              <S.SectionTitle>
                <BusinessIcon /> Identificação & CRM
              </S.SectionTitle>

              <S.StyledTextField
                fullWidth
                label="Nome da Empresa"
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                variant="outlined"
                required
                slotProps={{
                  input: {
                    startAdornment: (
                      <InputAdornment position="start">
                        <BusinessIcon />
                      </InputAdornment>
                    ),
                  },
                }}
              />

              <S.FormRow>
                <S.StyledTextField
                  label="CNPJ"
                  name="cnpj"
                  value={formData.cnpj}
                  onChange={handleInputChange}
                  variant="outlined"
                  required
                  slotProps={{
                    input: {
                      startAdornment: (
                        <InputAdornment position="start">
                          <CnpjIcon />
                        </InputAdornment>
                      ),
                    },
                  }}
                />
                <S.StyledTextField
                  label="ID Numérico do CRM"
                  name="company_id"
                  value={formData.company_id}
                  onChange={handleInputChange}
                  variant="outlined"
                  required
                  slotProps={{
                    input: {
                      startAdornment: (
                        <InputAdornment position="start">
                          <CrmIcon />
                        </InputAdornment>
                      ),
                    },
                  }}
                />
              </S.FormRow>

              <S.StyledTextField
                fullWidth
                label="E-mail de Contato"
                name="email"
                type="email"
                value={formData.email}
                onChange={handleInputChange}
                variant="outlined"
                required
                slotProps={{
                  input: {
                    startAdornment: (
                      <InputAdornment position="start">
                        <EmailIcon />
                      </InputAdornment>
                    ),
                  },
                }}
              />

              <S.SectionTitle>
                <WabaIcon /> Configurações de Conexão
              </S.SectionTitle>

              <S.FormRow>
                <S.StyledTextField
                  label="WABA ID"
                  name="waba_id"
                  value={formData.waba_id}
                  onChange={handleInputChange}
                  variant="outlined"
                  placeholder="Opcional"
                  slotProps={{
                    input: {
                      startAdornment: (
                        <InputAdornment position="start">
                          <WabaIcon />
                        </InputAdornment>
                      ),
                    },
                  }}
                />
                <S.StyledTextField
                  label="Phone Number ID"
                  name="phone_number_id"
                  value={formData.phone_number_id}
                  onChange={handleInputChange}
                  variant="outlined"
                  placeholder="Opcional"
                  slotProps={{
                    input: {
                      startAdornment: (
                        <InputAdornment position="start">
                          <PhoneIdIcon />
                        </InputAdornment>
                      ),
                    },
                  }}
                />
              </S.FormRow>
            </S.Column>

            <S.Column>
              <S.SectionTitle>
                <TokenIcon /> Credenciais Meta
              </S.SectionTitle>

              <S.StyledTextField
                fullWidth
                label="Meta Access Token"
                name="access_token"
                value={formData.access_token}
                onChange={handleInputChange}
                variant="outlined"
                required
                slotProps={{
                  input: {
                    startAdornment: (
                      <InputAdornment position="start">
                        <TokenIcon />
                      </InputAdornment>
                    ),
                  },
                }}
              />

              <S.FormRow>
                <S.StyledTextField
                  label="ID do App na Meta"
                  name="app_identify"
                  value={formData.app_identify}
                  onChange={handleInputChange}
                  variant="outlined"
                  required
                  slotProps={{
                    input: {
                      startAdornment: (
                        <InputAdornment position="start">
                          <AppIdIcon />
                        </InputAdornment>
                      ),
                    },
                  }}
                />
                <S.StyledTextField
                  label="Webhook Verify Token"
                  name="verify_token"
                  value={formData.verify_token}
                  onChange={handleInputChange}
                  variant="outlined"
                  required
                  slotProps={{
                    input: {
                      startAdornment: (
                        <InputAdornment position="start">
                          <VerifyIcon />
                        </InputAdornment>
                      ),
                    },
                  }}
                />
              </S.FormRow>

              <S.StyledTextField
                fullWidth
                label="Versão da API Meta"
                name="app_version"
                value={formData.app_version}
                onChange={handleInputChange}
                variant="outlined"
                required
                slotProps={{
                  input: {
                    startAdornment: (
                      <InputAdornment position="start">
                        <VersionIcon />
                      </InputAdornment>
                    ),
                  },
                }}
              />

              <S.SectionTitle>
                <MessagesIcon /> Limites & Operação
              </S.SectionTitle>

              <S.FormRow>
                <S.StyledTextField
                  label="Mensagens/Dia"
                  name="max_messages_per_day"
                  type="number"
                  value={formData.max_messages_per_day}
                  onChange={handleInputChange}
                  variant="outlined"
                  slotProps={{
                    input: {
                      startAdornment: (
                        <InputAdornment position="start">
                          <MessagesIcon />
                        </InputAdornment>
                      ),
                    },
                  }}
                />
                <S.StyledTextField
                  label="Requests/Sec (RPS)"
                  name="max_requests_per_second"
                  type="number"
                  value={formData.max_requests_per_second}
                  onChange={handleInputChange}
                  variant="outlined"
                  slotProps={{
                    input: {
                      startAdornment: (
                        <InputAdornment position="start">
                          <RpsIcon />
                        </InputAdornment>
                      ),
                    },
                  }}
                />
              </S.FormRow>

              <S.SwitchContainer>
                <Box>
                  <Typography
                    variant="body2"
                    sx={{ fontWeight: 700, color: "#fff" }}
                  >
                    WhatsApp Test Mode
                  </Typography>
                  <Typography
                    variant="caption"
                    sx={{ color: "rgba(255,255,255,0.5)" }}
                  >
                    Habilita comportamento de teste para esta empresa
                  </Typography>
                </Box>
                <Switch
                  name="whatsapp_test_mode"
                  checked={formData.whatsapp_test_mode}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      whatsapp_test_mode: e.target.checked,
                    }))
                  }
                  color="secondary"
                />
              </S.SwitchContainer>
            </S.Column>
          </S.SectionsGrid>

          <S.FooterSection>
            <S.SubmitButton
              type="submit"
              disabled={loading}
              sx={{ minWidth: "300px" }}
            >
              {loading ? (
                <CircularProgress size={24} color="inherit" />
              ) : (
                "Finalizar Cadastro"
              )}
            </S.SubmitButton>
          </S.FooterSection>
        </form>
      </S.GlassCard>
    </S.AdminSetupContainer>
  );
};

export default AdminCompanySetup;
